import { Injectable } from '@angular/core';
import { environment } from '@env/environment';

export interface QueryParams {
  [key: string]: any;
}

export enum ApiVersion {
  V2 = 'v2',
  V3 = 'v3',
}

/**
 * Please keep it in alphabetical order
 */
interface Env {
  login: string;
  benchmarks: string;
  ga: string;
  benchmarksService: string;
  clientConfig: (version?: ApiVersion) => string;
  clientConfigs: (version?: ApiVersion) => string;
  register: (version?: ApiVersion) => string;
  oauth: (version?: ApiVersion) => string;
  integrations: (version?: ApiVersion) => string;
  integration: (version?: ApiVersion) => string;
  clientIntegration: (version?: ApiVersion) => string;
  googleAnalytics: (version?: ApiVersion) => string;
  passwordReset: (version?: ApiVersion) => string;
}

const API_URL = `${environment.sweetDashboardAppUrl}/api`;

/**
 * URLs without '/' at the end
 * Please keep it in alphabetical order
 */
const env: Env = {
  login: '/api/login',
  benchmarks: '/api/benchmarks',
  ga: '/api/ga',
  benchmarksService: '/apiBenchmarks',
  clientConfig: (version = ApiVersion.V2) =>
    `${API_URL}/${version}/client/config`,
  clientConfigs: (version = ApiVersion.V2) =>
    `${API_URL}/${version}/client/configs`,
  register: (version = ApiVersion.V2) => `${API_URL}/${version}/register`,
  oauth: (version = ApiVersion.V2) => `${API_URL}/${version}/oauth`,
  integrations: (version = ApiVersion.V2) =>
    `${API_URL}/${version}/client/integrations`,
  integration: (version = ApiVersion.V2) => `${API_URL}/${version}/integration`,
  clientIntegration: (version = ApiVersion.V2) =>
    `${API_URL}/${version}/client/integration`,
  googleAnalytics: (version = ApiVersion.V2) => `${API_URL}/${version}/ga`,
  passwordReset: (version = ApiVersion.V2) =>
    `${API_URL}/${version}/passwordReset`,
};

@Injectable()
export class ApiConfigService {
  public loginUrl(path: string, qParams: QueryParams = {}): string {
    return this.apiLogin + this.preparePath(path, qParams);
  }

  public benchmarksUrl(path: string, qParams: QueryParams = {}): string {
    return this.apiBenchmarks + this.preparePath(path, qParams);
  }

  public gaUrl(path: string, qParams: QueryParams = {}): string {
    return this.apiGa + this.preparePath(path, qParams);
  }

  public clientIntegrationUrl(
    path: string,
    queryParams: QueryParams = {},
    version?: ApiVersion,
  ): string {
    return (
      this.apiClientIntegration(version) + this.preparePath(path, queryParams)
    );
  }

  public benchmarksServiceUrl(path: string, qParams: QueryParams = {}): string {
    return this.apiBenchmarksService + this.preparePath(path, qParams);
  }

  public clientConfigUrl(
    path: string,
    queryParams: QueryParams = {},
    version?: ApiVersion,
  ): string {
    return this.apiClientConfig(version) + this.preparePath(path, queryParams);
  }

  public clientConfigsUrl(
    path: string,
    queryParams: QueryParams = {},
    version?: ApiVersion,
  ): string {
    return this.apiClientConfigs(version) + this.preparePath(path, queryParams);
  }

  public registerUrl(
    path: string,
    queryParams: QueryParams = {},
    version?: ApiVersion,
  ): string {
    return this.apiRegister(version) + this.preparePath(path, queryParams);
  }

  public oauthUrl(
    path: string,
    queryParams: QueryParams = {},
    version?: ApiVersion,
  ): string {
    return this.apiOauth(version) + this.preparePath(path, queryParams);
  }

  public integrationsUrl(
    path: string,
    queryParams: QueryParams = {},
    version?: ApiVersion,
  ): string {
    return this.apiIntegrations(version) + this.preparePath(path, queryParams);
  }

  public integrationUrl(
    path: string,
    queryParams: QueryParams = {},
    version?: ApiVersion,
  ): string {
    return this.apiIntegration(version) + this.preparePath(path, queryParams);
  }

  public googleAnalyticsUrl(
    path: string,
    queryParams: QueryParams = {},
    version?: ApiVersion,
  ): string {
    return (
      this.apiGoogleAnalytics(version) + this.preparePath(path, queryParams)
    );
  }

  public passwordResetUrl(
    path: string,
    queryParams: QueryParams = {},
    version?: ApiVersion,
  ): string {
    return this.apiPasswordReset(version) + this.preparePath(path, queryParams);
  }

  private get apiLogin(): string {
    return env.login;
  }

  private get apiBenchmarks(): string {
    return env.benchmarks;
  }

  private get apiGa(): string {
    return env.ga;
  }

  private get apiBenchmarksService(): string {
    return env.benchmarksService;
  }

  private apiClientConfig(apiVersion?: ApiVersion): string {
    return env.clientConfig(apiVersion);
  }

  private apiClientConfigs(apiVersion?: ApiVersion): string {
    return env.clientConfigs(apiVersion);
  }

  private apiRegister(apiVersion?: ApiVersion): string {
    return env.register(apiVersion);
  }

  private apiOauth(apiVersion?: ApiVersion): string {
    return env.oauth(apiVersion);
  }

  private apiIntegrations(apiVersion?: ApiVersion): string {
    return env.integrations(apiVersion);
  }

  private apiIntegration(apiVersion?: ApiVersion): string {
    return env.integration(apiVersion);
  }

  private apiClientIntegration(apiVersion?: ApiVersion): string {
    return env.clientIntegration(apiVersion);
  }

  private apiGoogleAnalytics(apiVersion?: ApiVersion): string {
    return env.googleAnalytics(apiVersion);
  }

  private apiPasswordReset(apiVersion?: ApiVersion): string {
    return env.passwordReset(apiVersion);
  }

  private preparePath(path: string, queryParams: QueryParams = {}): string {
    const params: string[] = [];
    Object.keys(queryParams).forEach((key) => {
      if (queryParams[key] !== null && queryParams[key] !== void 0) {
        if (Array.isArray(queryParams[key])) {
          params.push(
            ...queryParams[key]
              .filter((v: any, i: any, a: string | any[]) => a.indexOf(v) === i)
              .map((item: string) => key + '[]=' + encodeURI(item)),
          );
        } else {
          params.push(key + '=' + encodeURI('' + queryParams[key]));
        }
      }
    });
    return (
      (path.length ? (path.substr(0, 1) === '/' ? path : `/${path}`) : '') +
      (params.length ? '?' + params.join('&') : '')
    );
  }
}
